import { cn } from "@/lib/utils"

import { ProductPanel } from "./product-panel"
import { TrainForm } from "./train-form"
import { type PartnerInfo } from "./types"

/**
 * Centered hero: the offer stated plainly, then the evidence beneath it.
 *
 * The copy is deliberately unclever. This template ships to hundreds of
 * partners across trades, clinics, studios and coworking, so it has to read
 * as a clear service offer under any brand — no narrative hook that only lands
 * for one vertical, and nothing that assumes a particular tone of voice.
 * Every job is named, because the jobs are what is being sold.
 */
export function Hero({
  partner,
  onTrain,
}: {
  partner: PartnerInfo
  onTrain: (website: string, company: string) => Promise<string | null>
}) {
  return (
    <section className="w-full px-4 pt-10 sm:px-8 sm:pt-16">
      <div className="mx-auto flex w-full max-w-5xl flex-col items-center">
        <div className="flex w-full flex-col items-center text-center">
          {/* The relationship line ("A member benefit from …") is what recasts
              the page as the partner's offer rather than a product's. IMP-499:
              a partner who hasn't set one gets no badge — an empty pill, or a
              generic label standing in for a relationship, is worse than the
              hero simply opening on its heading. */}
          {partner.badge && (
            <span className="border-lp-border text-lp-fg inline-flex items-center gap-2 rounded-full border bg-white px-4 py-1.5 text-sm font-medium">
              <span
                className="size-1.5 rounded-full"
                style={{ background: "var(--lp-accent)" }}
              />
              {partner.badge}
            </span>
          )}

          {/* mt only when a badge sits above; otherwise the heading opens the
              page and the section's own top padding is the spacing. */}
          <h1
            className={cn(
              "font-lp-serif text-lp-fg max-w-[20ch] text-[clamp(2.25rem,4.5vw,3.5rem)] leading-[1.04] tracking-[-0.025em] text-balance",
              partner.badge && "mt-8",
            )}
          >
            {partner.heading}
          </h1>

          <p className="text-lp-muted mt-6 max-w-[52ch] text-base leading-[1.65] text-pretty whitespace-pre-line">
            {partner.subheading}
          </p>

          {/* IMP-502: the discovery entry — the URL field that launches the
              free assessment overlay. */}
          <div className="mt-10 flex w-full flex-col items-center">
            <TrainForm onTrain={onTrain} />
          </div>
        </div>

        <div className="mt-14 w-full sm:mt-20">
          <ProductPanel />
        </div>
      </div>
    </section>
  )
}
